import React, { FC, useState } from 'react';

// Hooks
import useCgiPolling from '../../../hooks/useCgiPolling';

// Models
import {
  CgiProgressStatusType,
  CgiProvider,
  PollingConfig,
} from '../../../models/CGI/CGI';

// Components
import CgiSameDevice from '../CgiSameDevice';
import CgiAnotherDevice from '../CgiAnotherDevice';

// Utils
import {
  getAuthenticationTypeFromCgiAuthenticationOption,
  getCgiProvider,
  getCgiProviderName,
} from '../../../utilities/cgi';
import { CircleLoader } from '../../common';

// i18n
import { useTranslation } from '../../../translations/useTranslation';

interface PollerProps extends PollingConfig {
  onSuccess: (accessToken: string) => void;
  onReset: () => void;
}

const CgiPoller: FC<PollerProps> = ({
  collectRequest,
  authenticationOption,
  autoStartToken,
  onSuccess,
  onReset,
}) => {
  // Activate polling on mount
  const [pollingActive, setPollingActive] = useState<boolean>(true);

  const { t, m, keys } = useTranslation();
  const { data, isError } = useCgiPolling({
    request: collectRequest,
    enabled: pollingActive,
    onError: () => setPollingActive(false),
    onSuccess: (response) => {
      if (response?.data?.ProgressStatus === CgiProgressStatusType.COMPLETE) {
        setPollingActive(false);
        if (response.data.AccessToken) {
          onSuccess(response.data.AccessToken);
        }
      }
    },
  });

  if (!collectRequest) {
    return <CircleLoader size="sm" />;
  }

  const getLogoUrl = (): string => {
    switch (collectRequest?.provider) {
      case CgiProvider.Freja:
        return '/img/freja-logo.png';
      case CgiProvider.BankId:
        return '/img/bankid-logo.png';
      default:
        return '';
    }
  };

  const providerName = getCgiProviderName(collectRequest.provider);
  const provider = getCgiProvider(collectRequest.provider);
  const authenticationType =
    getAuthenticationTypeFromCgiAuthenticationOption(authenticationOption);

  const getPollingView = () => {
    if (isError) {
      return <p className="cgi-message">{t(keys.cgiErrorGeneric)}</p>;
    }

    if (data?.ProgressStatus === CgiProgressStatusType.COMPLETE) {
      return (
        <>
          {' '}
          <p className="cgi-message cgi-message--success">
            Legitimeringen lyckades. Vänligen vänta.
          </p>
          <CircleLoader size="sm" />
        </>
      );
    }

    if (authenticationType === 'same-device') {
      return (
        <CgiSameDevice
          provider={provider}
          progressStatus={data?.ProgressStatus}
          autoStartToken={autoStartToken}
        />
      );
    } else {
      return (
        <CgiAnotherDevice
          provider={provider}
          progressStatus={data?.ProgressStatus}
          qrCodeData={data?.QrCodeData}
        />
      );
    }
  };

  return (
    <div className="form-app__cgi-poller">
      <img src={getLogoUrl()} />
      <h2>{m(keys.cgiTitle, '{provider}', providerName)}</h2>
      {getPollingView()}
      {data?.ProgressStatus !== CgiProgressStatusType.COMPLETE && (
        <button
          className="button button-subtle poller-cancel-button"
          onClick={onReset}
        >
          {isError ? t(keys.uiTryAgain) : t(keys.uiCancel)}
        </button>
      )}
    </div>
  );
};

export default CgiPoller;
